// Base color palette - all primary colors defined here
const palette = {
  // Brand Colors (tonalidade específica para identidade da marca)
  brand: {
    50: '#eefbf2',
    100: '#d7f5e1',
    200: '#b2eac8',
    300: '#81daa5',
    400: '#4bc47d',
    500: '#26ab5d',
    600: '#1a9049',
    700: '#16753c',
    800: '#155d32',
    900: '#134d2a',
  },
  // Green (para casos de sucesso e outros usos)
  green: {
    50: '#f2fdf5',  
    100: '#dcfce7', 
    200: '#e0fae7', 
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#4c8a55',  
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
  },
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fae2e1',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#cb3c33',  
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  orange: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316',
    600: '#ea580c',
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
  },
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  yellow: {
    50: '#fefce8',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fde047',
    400: '#facc15',
    500: '#eab308',
    600: '#ca8a04',
    700: '#a16207',
    800: '#854d0e',
    900: '#713f12',
  },
  white: '#ffffff',
} as const

export const colors = {
  // Semantic Brand Colors
  brand: {
    primary: palette.brand[600],
    border: palette.brand[600],
    secondary: palette.brand[700], 
    tertiary: palette.brand[800],
  },

  // Semantic Background Colors
  background: {
    primary: palette.white,
    secondary: palette.slate[50],
    white: palette.white,
    positive: palette.green[100],
    negative: palette.red[50],
    info: palette.blue[50],
    warning: palette.yellow[50],
    // Legacy support - to be deprecated
    success: palette.green[100],
    error: palette.red[50],
  },

  // Semantic Text Colors
  text: {
    primary: palette.slate[900],
    secondary: palette.slate[600],
    tertiary: palette.slate[400],
    positive: palette.green[700],
    negative: palette.red[600],
    warning: palette.yellow[600],
    info: palette.blue[600],
    // Legacy support - to be deprecated
    success: palette.green[700],
    error: palette.red[600],
  },

  // Semantic Game Level Colors
  gameLevel: {
    beginner: {
      background: palette.green[50],
      text: palette.green[700],
      border: palette.green[200],
    },
    intermediate: {
      background: palette.blue[50],
      text: palette.blue[700],
      border: palette.blue[200],
    },
    advanced: {
      background: palette.purple[50],
      text: palette.purple[700],
      border: palette.purple[200],
    },
    professional: {
      background: palette.red[50],
      text: palette.red[700],
      border: palette.red[200],
    },
    open: {
      background: palette.gray[50],
      text: palette.gray[700],
      border: palette.gray[200],
    },
  },

  // Semantic Vacancy Status Colors
  gameVacancy: {
    available: {
      background: palette.green[50],
      text: palette.green[600],
    },
    limited: {
      background: palette.orange[50],
      text: palette.orange[600],
    },
    full: {
      background: palette.red[50],
      text: palette.red[600],
    },
  },

  // Semantic Border Colors
  border: {
    primary: palette.gray[300],     // Melhor contraste
    secondary: palette.gray[400],   // Contraste médio
    light: palette.gray[200],       // Contraste leve
    subtle: palette.gray[200],      // Removendo opacidade
  },

  // Semantic Interactive Colors
  interactive: {
    hover: palette.slate[50],
    active: palette.brand[50],
    disabled: palette.gray[100],
    focus: palette.brand[200],
  },

  // Semantic Status Colors (extended)
  status: {
    positive: {
      background: palette.green[50],  // verde claro
      text: palette.green[500],       // verde escuro
      border: palette.green[300],     // verde medium
    },
    negative: {
      background: palette.red[50],    // vermelho claro
      text: palette.red[500],         // vermelho escuro
      border: palette.red[300],       // vermelho medium
    },
    warning: {
      background: palette.yellow[50],
      text: palette.yellow[600],
      border: palette.yellow[300],
    },
    info: {
      background: palette.blue[50],
      text: palette.blue[700],
      border: palette.blue[200],
    },
    // Legacy support - to be deprecated
    success: {
      background: palette.green[50],
      text: palette.green[500],
      border: palette.green[300],
    },
    error: {
      background: palette.red[50],
      text: palette.red[500],
      border: palette.red[300],
    },
  },

  // Direct palette access for advanced use cases
  palette: {
    brand: palette.brand,
    green: palette.green,
    blue: palette.blue,
    purple: palette.purple,
    red: palette.red,
    orange: palette.orange,
    gray: palette.gray,
    slate: palette.slate,
    yellow: palette.yellow,
    white: palette.white,
  },

  // New semantic color shortcuts
  positive: palette.green[600],
  negative: palette.red[600],

  // Legacy/Utility - to be deprecated
  success: palette.green[600],
  error: palette.red[600],
  warning: palette.yellow[600],
  info: palette.blue[600],
  gray: palette.gray,
} as const

export type Colors = typeof colors